import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Spinner,
  Badge,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { CreditCardPerson24Regular, Warning20Filled } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from './DataTable';
import { queryKeys } from '../lib/queryClient';
import { apiFetch } from '../lib/apiFetch';

interface CustomerPaymentMethodsPanelProps {
  customerId: string;
}

/** One saved card from Auth.Net CIM, as returned by
 *  GET /customers/:id/saved-cards. `expiration` is "YYYY-MM" when AN
 *  unmasks it (see the connector); falls back to "XXXX" / "" otherwise. */
interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expiration?: string;
}

interface SavedCardsResponse {
  data: SavedCard[];
  /** Present when the AN CIM lookup failed — the backend returns 200 with an
   *  empty list rather than erroring, so we surface the reason inline. */
  warning?: string;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
    flex: 1,
    minHeight: 0,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: '8px',
    ...shorthands.padding('32px', '12px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  emptyIcon: {
    width: '28px',
    height: '28px',
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    ...shorthands.padding('10px', '12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  warningIcon: {
    color: tokens.colorPalettePumpkinBorderActive,
    flexShrink: 0,
  },
  cardNumber: {
    fontFamily: 'monospace',
  },
});

/** AN gives expiration as "YYYY-MM" (unmasked) — render it as MM/YY. Anything
 *  else (masked "XXXX", empty) renders as an em dash. */
function formatExpiration(raw?: string): string {
  if (!raw) return '—';
  const match = raw.match(/^(\d{4})-(\d{2})$/);
  if (!match) return '—';
  const [, year, month] = match;
  return `${month}/${year.slice(2)}`;
}

const CustomerPaymentMethodsPanel: React.FC<CustomerPaymentMethodsPanelProps> = ({ customerId }) => {
  const styles = useStyles();

  const { data, isLoading, error } = useQuery<SavedCardsResponse>({
    queryKey: queryKeys.customers.savedCards(customerId),
    queryFn: async () => {
      const res = await apiFetch(`/customers/${encodeURIComponent(customerId)}/saved-cards`);
      if (!res.ok) throw new Error(`Failed to load payment methods: ${res.statusText}`);
      return res.json();
    },
    enabled: !!customerId,
    staleTime: 60 * 1000,
  });

  const cards = data?.data ?? [];
  const warning = data?.warning;

  const columns: ColumnConfig<SavedCard>[] = [
    {
      columnKey: 'brand',
      label: 'Brand',
      sortable: true,
      filterable: false,
      width: '160px',
      render: (card) => card.brand || 'Card',
    },
    {
      columnKey: 'last4',
      label: 'Card number',
      sortable: false,
      filterable: false,
      render: (card) => (
        <span className={styles.cardNumber}>
          {card.last4 ? `•••• ${card.last4}` : '—'}
        </span>
      ),
    },
    {
      columnKey: 'expiration',
      label: 'Expiration',
      sortable: true,
      filterable: false,
      width: '120px',
      render: (card) => formatExpiration(card.expiration),
    },
  ];

  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.loading}>
          <Spinner size="medium" label="Loading payment methods…" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>
          {error instanceof Error ? error.message : 'Failed to load payment methods'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {warning && (
        <div className={styles.warning}>
          <Warning20Filled className={styles.warningIcon} />
          <span>Could not reach Auth.Net to confirm saved cards: {warning}</span>
        </div>
      )}

      {cards.length === 0 ? (
        <div className={styles.empty}>
          <CreditCardPerson24Regular className={styles.emptyIcon} />
          <span>
            No saved payment methods on file. Cards are captured securely when
            the customer pays an invoice via their pay link — they can&rsquo;t
            be added manually here.
          </span>
        </div>
      ) : (
        <DataTable<SavedCard>
          data={cards}
          columns={columns}
          keyField="id"
          hideableColumns
          columnVisibilityStorageKey="customer_payment_methods"
          itemsPerPage={25}
          primaryColumnKey="brand"
          disableWrapper={true}
        />
      )}
    </div>
  );
};

export default CustomerPaymentMethodsPanel;
